// src/pages/ProviderProfilePage.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, updateDoc } from 'firebase/firestore';

// Now your app CSS
import '../styles/forms.css';
import '../styles/components/profile-pages.css';

// Local imports
import { db } from '../services/firebase';
import { useAuth } from '../contexts/AuthContext';
import { useMessaging } from '../contexts/MessagingContext';
import ProviderReviews from '../components/provider/ProviderReviews';
import StarRating from '../components/common/StarRating';
import ProviderEditForm from '../components/provider/ProviderEditForm';
import WaitlistForm from '../components/parent/WaitlistForm';
import FoodMenuAccordion from '../components/provider/FoodMenuAccordion';

const ProviderProfilePage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentUser, isParent, userProfile } = useAuth();
  const { startConversation } = useMessaging();
  
  const [provider, setProvider] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('details');
  const [isEditing, setIsEditing] = useState(false);
  const [showWaitlistForm, setShowWaitlistForm] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImg, setLightboxImg] = useState(null);
  
  const isOwner = currentUser && provider && currentUser.uid === provider.uid;
  const isPublicView = !currentUser;
  
  const profileId = id || provider?.id || provider?.uid;
  
  // Fetch provider data
  useEffect(() => {
    const fetchProvider = async () => {
      try {
        // If no ID is provided and user is a provider, show their own profile
        const providerId = id || (currentUser?.uid);
        
        if (!providerId) {
          setError('Provider not found');
          setLoading(false);
          return;
        }

        const providerDoc = await getDoc(doc(db, 'providers', providerId));
        
        if (providerDoc.exists()) {
          setProvider({ id: providerDoc.id, ...providerDoc.data() });
        } else {
          setError('Provider not found');
        }
      } catch (err) {
        console.error('Error fetching provider:', err);
        setError('Failed to load provider information');
      } finally {
        setLoading(false);
      }
    };
    
    fetchProvider();
  }, [id, currentUser]);
  
  // Handle send message
  const handleSendMessage = async () => {
    if (!currentUser || !provider) return;
    
    try {
      const conversation = await startConversation(provider.uid);
      if (conversation && conversation.id) {
        // Navigate to the specific conversation
        navigate(`/messages/${conversation.id}`);
      } else {
        // Fallback to messages page if conversation creation failed
        navigate('/messages');
      }
    } catch (err) {
      console.error('Error starting conversation:', err);
    }
  };

  // Handle profile update
  const handleProfileUpdate = async (updatedProfile) => {
    try {
      // Get the provider document reference
      const providerRef = doc(db, 'providers', provider.id);

      // Map ageGroupCapacities to maxCapacity for display compatibility
      const maxCapacity = {
        infant: updatedProfile.ageGroupCapacities?.['Infant/Toddler'] || 0,
        preschool: updatedProfile.ageGroupCapacities?.['Preschool'] || 0,
        schoolAge: updatedProfile.ageGroupCapacities?.['School-Age'] || 0
      };

      // Ensure licensed is always a boolean
      const licensed = Boolean(updatedProfile.licensed === true || updatedProfile.licensed === 'true');

      // Update the profile in Firestore
      await updateDoc(providerRef, {
        displayName: updatedProfile.displayName,
        profile: {
          ...provider.profile,
          ...updatedProfile,
          displayName: updatedProfile.displayName,
          licensed, // always boolean
          maxCapacity,
          screenTimePolicy: updatedProfile.screenTimePolicy || '',
          foodMenu: updatedProfile.foodMenu || '',
          certifications: updatedProfile.certifications || [],
        }
      });

      // Update the local state
      setProvider(prev => ({
        ...prev,
        profile: {
          ...prev.profile,
          ...updatedProfile,
          licensed, // always boolean
          maxCapacity,
          screenTimePolicy: updatedProfile.screenTimePolicy || '',
          foodMenu: updatedProfile.foodMenu || '',
          certifications: updatedProfile.certifications || [],
        }
      }));
      setIsEditing(false);
    } catch (err) {
      console.error('Error updating profile:', err);
      setError('Failed to update profile');
    }
  };
  
  // Helper to format time in 12-hour format
  const formatTime12Hour = (timeStr) => {
    if (!timeStr) return '';
    const [hour, minute] = timeStr.split(':');
    const date = new Date();
    date.setHours(Number(hour), Number(minute));
    return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true });
  };

  const daysOfWeek = [
    'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'
  ];
  
  const handleShare = () => {
    const url = `${window.location.origin}/provider/${profileId}`;
    if (navigator.share) {
      navigator.share({
        title: provider?.profile?.displayName || 'Provider Profile',
        url,
      });
    } else {
      navigator.clipboard.writeText(url);
      alert('Profile link copied to clipboard!');
    }
  };
  
  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading provider profile...</p>
      </div>
    );
  }
  
  if (error || !provider) {
    return (
      <div className="error-container">
        <h2>Error</h2>
        <p>{error || 'Failed to load provider profile'}</p>
        {!id && currentUser && (
          <button 
            className="btn btn-primary"
            onClick={() => navigate('/provider/dashboard')}
          >
            Return to Dashboard
          </button>
        )}
      </div>
    );
  }
  
  const providerProfile = provider.profile || {};
  
  const foodMenu = Array.isArray(providerProfile.foodMenu)
    ? providerProfile.foodMenu
    : providerProfile.foodMenu
      ? [providerProfile.foodMenu]
      : [];
  
  return (
    <div className="provider-profile-page">
      {isPublicView && (
        <div className="public-profile-banner" style={{background:'#f7f7fa',color:'#444',padding:'1rem',borderRadius:'10px',marginBottom:'1.5rem',textAlign:'center',fontWeight:600}}>
          This is a public profile view. Log in to contact or interact.
        </div>
      )}
      <div className="profile-header">
        <div className="header-content">
          <h1>{provider.profile?.displayName || 'Provider'}</h1>
          
          <div className="provider-meta">
            <div className="provider-rating">
              <StarRating value={provider.averageRating || 0} readonly size="medium" />
              <span className="rating-count">
                ({provider.reviewCount || 0} reviews)
              </span>
            </div>
            
            <div className="provider-type">
              {providerProfile.type === 'center' ? 'Daycare Center' : 
               providerProfile.type === 'home' ? 'Daycare Home' : 'Childcare Provider'}
              {providerProfile.licensed && ' • Licensed'}
            </div>
          </div>
        </div>
        
        <div className="header-actions">
          {currentUser && isOwner ? (
            <button 
              className="edit-button"
              onClick={() => setIsEditing(!isEditing)}
            >
              {isEditing ? 'Cancel Edit' : 'Edit Profile'}
            </button>
          ) : currentUser && !isOwner && (
            <>
              <button 
                className="contact-button" 
                onClick={handleSendMessage}
              >
                Contact Provider
              </button>
              {isParent && typeof isParent === 'function' && isParent() && (
                <>
                  <button className="join-waitlist-button" onClick={() => setShowWaitlistForm(true)}>
                    Join Waitlist
                  </button>
                  {showWaitlistForm && (
                    <WaitlistForm 
                      provider={provider}
                      onClose={() => setShowWaitlistForm(false)}
                    />
                  )}
                </>
              )}
            </>
          )}
          <button className="share-profile-btn" onClick={handleShare}>Share Profile</button>
        </div>
      </div>

      {isEditing ? (
        <ProviderEditForm
          profile={providerProfile}
          onSave={handleProfileUpdate}
          onCancel={() => setIsEditing(false)}
        />
      ) : (
        <>
          <div className="profile-tabs">
            <button 
              className={`tab-button ${activeTab === 'details' ? 'active' : ''}`}
              onClick={() => setActiveTab('details')}
            >
              Details
            </button>
            <button 
              className={`tab-button ${activeTab === 'programs' ? 'active' : ''}`}
              onClick={() => setActiveTab('programs')}
            >
              Programs & Activities
            </button>
            <button 
              className={`tab-button ${activeTab === 'gallery' ? 'active' : ''}`}
              onClick={() => setActiveTab('gallery')}
            >
              Gallery
            </button>
            <button 
              className={`tab-button ${activeTab === 'reviews' ? 'active' : ''}`}
              onClick={() => setActiveTab('reviews')}
            >
              Reviews
            </button>
          </div>
          
          <div className="profile-content">
            {activeTab === 'details' && (
              <div className="profile-details">
                <div className="details-grid">
                  <div className="details-section">
                    <h2>Contact Information</h2>
                    {providerProfile.contactPreference && isOwner && (
                      <p><strong>Preferred Contact:</strong> {providerProfile.contactPreference}</p>
                    )}
                    {providerProfile.phone && isOwner && (
                      <p><strong>Phone:</strong> {providerProfile.phone}</p>
                    )}
                    {isOwner && <p><strong>Email:</strong> {provider.email || 'Email not provided'}</p>}
                  </div>
                  
                  <div className="details-section">
                    <h2>Location</h2>
                    {providerProfile.address && isOwner && (
                      <p><strong>Address:</strong> {providerProfile.address}</p>
                    )}
                  </div>
                  
                  <div className="details-section">
                    <h2>Hours of Operation</h2>
                    {providerProfile.hoursOfOperation ? (
                      <div className="hours-grid">
                        {daysOfWeek.map(day => {
                          const key = day.toLowerCase();
                          const hours = providerProfile.hoursOfOperation[key];
                          return (
                            <div className="day-hours" key={day}>
                              <div className="day-name">{day}</div>
                              <div className="hours">
                                {hours && hours.open && hours.close ? (
                                  `${formatTime12Hour(hours.open)} - ${formatTime12Hour(hours.close)}`
                                ) : (
                                  'Closed'
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <p>Hours not specified</p>
                    )}
                  </div>
                  
                  <div className="details-section">
                    <h2>Age Groups & Capacity</h2>
                    {providerProfile.ageGroups && Array.isArray(providerProfile.ageGroups) && providerProfile.ageGroups.length > 0 ? (
                      <div className="age-groups">
                        {providerProfile.ageGroups.map(ageGroup => (
                          <div className="age-group-item" key={ageGroup}>
                            <span className="age-group-name">{ageGroup}</span>
                            {providerProfile.maxCapacity && (
                              <span className="capacity">
                                Max capacity: {
                                  ageGroup === 'Infant/Toddler' ? (providerProfile.maxCapacity.infant || 0) :
                                  ageGroup === 'Preschool' ? (providerProfile.maxCapacity.preschool || 0) :
                                  ageGroup === 'School-Age' ? (providerProfile.maxCapacity.schoolAge || 0) : 0
                                }
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p>No age groups specified</p>
                    )}
                  </div>
                  
                  <div className="details-section">
                    <h2>Languages</h2>
                    {providerProfile.languages && Array.isArray(providerProfile.languages) && providerProfile.languages.length > 0 ? (
                      <div className="languages-list">
                        {providerProfile.languages.map(language => (
                          <span className="language-tag" key={language}>{language}</span>
                        ))}
                      </div>
                    ) : (
                      <p>No languages specified</p>
                    )}
                  </div>
                  
                  <div className="details-section">
                    <h2>Fees</h2>
                    {providerProfile.fees ? (
                      <div className="fees-list">
                        {providerProfile.fees.hourly && (
                          <div className="fee-item">
                            <span className="fee-type">Hourly:</span>
                            <span className="fee-amount">${providerProfile.fees.hourly}</span>
                          </div>
                        )}
                        {providerProfile.fees.daily && (
                          <div className="fee-item">
                            <span className="fee-type">Daily:</span>
                            <span className="fee-amount">${providerProfile.fees.daily}</span>
                          </div>
                        )}
                        {providerProfile.fees.monthly && (
                          <div className="fee-item">
                            <span className="fee-type">Monthly:</span>
                            <span className="fee-amount">${providerProfile.fees.monthly}</span>
                          </div>
                        )}
                      </div>
                    ) : (
                      <p>No fees specified</p>
                    )}
                  </div>
                </div>
              </div>
            )}
            
            {activeTab === 'programs' && (
              <div className="programs-activities">
                <div className="programs-section">
                  <h2>Programs</h2>
                  {providerProfile.programs && Array.isArray(providerProfile.programs) && providerProfile.programs.length > 0 ? (
                    <div className="programs-list">
                      {providerProfile.programs.map(program => (
                        <div className="program-item" key={program}>
                          <span className="program-name">{program}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p>No programs specified</p>
                  )}
                </div>
                
                <div className="activities-section">
                  <h2>Activities</h2>
                  {providerProfile.activities && Array.isArray(providerProfile.activities) && providerProfile.activities.length > 0 ? (
                    <div className="activities-list">
                      {providerProfile.activities.map(activity => (
                        <div className="activity-item" key={activity}>
                          <span className="activity-name">{activity}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p>No activities specified</p>
                  )}
                </div>
                
                <div className="screen-time-section">
                  <h2>Screen Time Policy</h2>
                  <p>{providerProfile.screenTimePolicy || (providerProfile.screenTime ? 'Screen time is included in the program' : 'No screen time is provided')}</p>
                </div>
                
                <div className="food-menu-section">
                  <h2>Food Menu</h2>
                  {foodMenu.length > 0 ? (
                    <FoodMenuAccordion foodMenu={foodMenu} />
                  ) : (
                    <div>No food menu available.</div>
                  )}
                </div>
                
                <div className="certifications-section">
                  <h2>Certifications & Education</h2>
                  {providerProfile.education && (
                    <div className="education">
                      <h3>Education</h3>
                      <p>{providerProfile.education}</p>
                    </div>
                  )}
                  
                  {providerProfile.certifications && Array.isArray(providerProfile.certifications) && providerProfile.certifications.length > 0 ? (
                    <div className="certifications">
                      <h3>Certifications</h3>
                      <div className="certifications-list">
                        {providerProfile.certifications.map(certification => (
                          <div className="certification-item" key={certification}>
                            <span className="certification-name">{certification}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <p>No certifications specified</p>
                  )}
                </div>
              </div>
            )}
            
            {activeTab === 'gallery' && providerProfile.photos && providerProfile.photos.length > 0 && (
              <div className="profile-gallery-tab">
                <h2>Photo Gallery</h2>
                <div className="photo-collage-grid">
                  {providerProfile.photos.map((url, idx) => (
                    <div key={url} className="collage-photo-thumb">
                      <img src={url} alt={`Provider photo ${idx + 1}`} onClick={() => { setLightboxImg(url); setLightboxOpen(true); }} style={{ cursor: 'pointer' }} />
                    </div>
                  ))}
                </div>
                {lightboxOpen && lightboxImg && (
                  <div className="lightbox-overlay" onClick={() => setLightboxOpen(false)}>
                    <div className="lightbox-modal" onClick={e => e.stopPropagation()}>
                      <button className="lightbox-close" onClick={() => setLightboxOpen(false)}>×</button>
                      <img src={lightboxImg} alt="Large provider" className="lightbox-img" />
                    </div>
                  </div>
                )}
              </div>
            )}
            
            {activeTab === 'reviews' && (
              <ProviderReviews 
                providerId={provider.uid} 
                providerName={provider.displayName || 'Provider'} 
              />
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default ProviderProfilePage;